import {
  combineSlices,
  configureStore,
  createListenerMiddleware,
  type ThunkAction,
  type UnknownAction,
} from '@reduxjs/toolkit';
import { registerAnalyticsListeners, tableViewsSlice } from '@/features/analytics';
import { registerAuthListeners } from '@/features/auth';
import {
  filtersSlice,
  prefsSlice,
  registerPortalListeners,
  uiSlice,
  viewsSlice,
} from '@/features/portal';
import { baseApi } from '@/shared/api';
import { sessionSlice } from '@/shared/session';

/**
 * Root reducer. Server data lives in RTK Query (`api`); slices hold client state only:
 * session, global filters, preferences, UI (drawer, menu, toasts), saved list views and the
 * viewer's data explorer table views.
 */
export const rootReducer = combineSlices(
  baseApi,
  sessionSlice,
  filtersSlice,
  prefsSlice,
  uiSlice,
  viewsSlice,
  tableViewsSlice,
);

export type RootState = ReturnType<typeof rootReducer>;

/**
 * Store factory — the app creates one store with persisted state; tests create a fresh store per test.
 */
export function setupStore(preloadedState?: Partial<RootState>) {
  const listenerMiddleware = createListenerMiddleware();

  const store = configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().prepend(listenerMiddleware.middleware).concat(baseApi.middleware),
  });

  const startListening = listenerMiddleware.startListening.withTypes<
    RootState,
    typeof store.dispatch
  >();
  registerAuthListeners(startListening);
  registerPortalListeners(startListening);
  registerAnalyticsListeners(startListening);

  return store;
}

export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore['dispatch'];
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  UnknownAction
>;
